import {
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  FeedbackIcon,
  LogoutIcon,
  PersonInCircleIcon,
  ReportIcon,
  ReviewsIcon,
} from "../images";
import { useOutsideClick } from "../../hooks/useOutSideClick";
import { CustomButton } from "../ui/CustomButton";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AppRoutes, UserRoles } from "../../types/enums";
import { useGetUserQuery } from "../../api";
import { ProfileAvatar } from "./ProfileAvatar";
import { useLogout } from "../../hooks";
import { ExportEmailsButton } from "./ExportEmailsButton";
import { pushUserDataToGTM } from "../../utils/pushUserDataToGTM";
import { MenuNavButton } from "../ui/buttons";
import { FeedbackModal } from "../ui/modal";

export const ProfileButton = () => {
  const { account_id, campaign_id } = useParams();
  const { pathname } = useLocation();
  const logout = useLogout();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFeedbackModal, setIsFeedbackModal] = useState<boolean>(false);

  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: user } = useGetUserQuery();

  const closeHandler = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (user) {
      void pushUserDataToGTM(user.email);
    }
  }, [user]);

  useOutsideClick({
    elementRef: dropdownRef,
    onOutsideClick: closeHandler,
    isOpened: isOpen,
    wrapperRef: dropdownWrapperRef,
  });

  const logoutHandler = () => {
    logout();
  };

  const goToProfileHandler = () => {
    pathname !== AppRoutes.PROFILE && navigate(AppRoutes.PROFILE);
    setIsOpen(false);
  };

  const isAdmin = user?.roles.includes(UserRoles.admin);

  return (
    <Stack ref={dropdownWrapperRef} gap="16px" alignItems="flex-start">
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: 0,
          border: "none",
          cursor: "pointer",
          background: "none",
          display: "flex",
        }}
      >
        <ProfileAvatar
          avatar_url={user?.avatar_url}
          height="42px"
          width="42px"
        />
      </button>

      {isOpen && (
        <Stack
          ref={dropdownRef}
          sx={{
            backgroundColor: "white",
            position: "absolute",
            zIndex: 101,
            right: 0,
            top: 80,
            boxShadow: "0px 4px 15px 0px #00000040",
            borderRadius: "8px",
            padding: "24px 0 8px 0",
            width: "260px",
            gap: "8px",
          }}
        >
          <IconButton
            onClick={() => setIsOpen(false)}
            size="small"
            sx={{
              ml: "auto",
              color: "#828282",
              position: "absolute",
              top: "8px",
              right: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Stack gap="12px" alignItems="center" justifyContent="center">
            <ProfileAvatar avatar_url={user?.avatar_url} />
            <Stack gap="4px" alignItems="center">
              <Typography
                variant="h4"
                color="custom.textDarkBlue"
                fontSize="16px"
                lineHeight="22px"
                fontWeight={500}
                sx={{ letterSpacing: "0.3px", textAlign: "center" }}
              >
                {`${user?.first_name} ${user?.last_name}`}
              </Typography>
              <Typography
                variant="caption"
                color="#4C545B"
                sx={{ textAlign: "center" }}
              >
                {`${user?.company_name}`}
              </Typography>
            </Stack>
            <CustomButton
              onClick={goToProfileHandler}
              variant="outlined"
              customColor="#FFFFFF"
              textColor="#1D3E70"
              customSize="fit"
              type="button"
              sx={{ height: "42px", borderColor: "#1D3E70" }}
              startIcon={<PersonInCircleIcon />}
            >
              My Profile
            </CustomButton>
          </Stack>

          {isAdmin && (
            <>
              <Divider
                style={{ paddingTop: "8px", alignSelf: "stretch" }}
                flexItem
              />
              <Stack>
                <ExportEmailsButton />
                <MenuNavButton
                  handler={() => {
                    setIsOpen(false);
                    setIsFeedbackModal(true);
                  }}
                  text="Submit Feedback"
                  icon={<FeedbackIcon />}
                />
                <MenuNavButton
                  handler={() => {
                    setIsOpen(false);
                    navigate(AppRoutes.REVIEWS);
                  }}
                  text="Reviews"
                  icon={<ReviewsIcon />}
                />
                {account_id && (
                  <Link
                    to={`/${campaign_id}${AppRoutes.ACCOUNT_PLAN}/${account_id}`}
                    onClick={() => setIsOpen(false)}
                    style={{ textDecoration: "none", color: "#4C545B" }}
                  >
                    <ListItemButton
                      sx={{
                        gap: "12px",
                        justifyContent: "flex-start",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "10px" }}>
                        <ReportIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            fontWeight={500}
                            fontSize="14px"
                            lineHeight="20px"
                            sx={{ color: "#2194D2" }}
                          >
                            Generate Report
                            <span
                              style={{ color: "#A5A9AC", paddingLeft: "4px" }}
                            >
                              (Demo)
                            </span>
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </Link>
                )}
              </Stack>
            </>
          )}

          <Divider style={{ alignSelf: "stretch" }} flexItem />
          {!isAdmin && (
            <MenuNavButton
              handler={() => {
                setIsOpen(false);
                setIsFeedbackModal(true);
              }}
              text="Submit Feedback"
              icon={<FeedbackIcon />}
            />
          )}
          <MenuNavButton
            handler={logoutHandler}
            text="Log Out"
            icon={<LogoutIcon />}
          />
        </Stack>
      )}

      {isFeedbackModal && (
        <FeedbackModal
          isOpen={isFeedbackModal}
          setIsOpen={setIsFeedbackModal}
        />
      )}
    </Stack>
  );
};
